import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { useApiClient, type InferRequest } from '../../hooks/useApiClient'
import { useMasterMetadata } from '../useMetadata/useMasterMetadata'
import type { InvoiceCreationMetadataResponse } from '../useMetadata/types'
import { useMatchCacheUtils, type MatchQueryData } from './cacheUtils'

// Type aliases for invoice action requests
export type InvoiceVoidRequest = InferRequest<'/supplier_rec/invoice/void'>
export type InvoiceUnvoidRequest = InferRequest<'/supplier_rec/invoice/unvoid'>
export type CreateManualInvoiceRequest = InferRequest<'/supplier_rec/invoice/create_manual'>
export type CreateSupplierInvoiceV1Request = InferRequest<'/v1/supplier_reconciliation/invoice/create'>

// Types for invoice actions context
interface InvoiceActionContext {
  previousData: [unknown, unknown][] | undefined
  previousDetailData?: [unknown, unknown][]
  request: unknown
}

// Types for voided invoice structure
interface VoidedInvoice {
  _id: string
  invoice_id: string
  invoice_number: string
  total: number
  invoice_date: string
}

// Types for invoice documents
interface InvoiceDocument {
  document_id: string
  _id: string
  document_number: string
  total: number
  document_date: string
}

// Types for match detail data structure
interface MatchDetailData {
  void_invoices?: VoidedInvoice[]
  invoice_documents?: InvoiceDocument[]
  [key: string]: unknown
}

// Types for invoice creation detail
interface InvoiceDetail {
  type: 'supply' | 'tax' | 'freight' | string // Include common types to fix union comparison
  product?: {
    internal_name?: string
    source_name?: string
  }
  bol_number?: string
  total?: number
}

// Types for supplier and terminal in invoice creation
interface InvoiceSupplier {
  internal_id?: string
  internal_name?: string
  source_name?: string
}

interface InvoiceTerminal {
  internal_name?: string
  source_name?: string
}

// Types for invoice creation data
interface InvoiceCreationData {
  invoice_date: string
  due_date_utc: string
  details?: InvoiceDetail[]
  supplier?: InvoiceSupplier
  terminal?: InvoiceTerminal
  invoice_number?: string
  source_name?: string
}

/**
 * Hook for managing invoice actions (void, unvoid, create manual)
 * Extracted from page component to follow API hook patterns
 */
export const useInvoiceActions = () => {
  const queryClient = useQueryClient()
  const $api = useApiClient()
  const cacheUtils = useMatchCacheUtils(queryClient)

  /**
   * Invalidate match overview queries after invoice operations
   */
  const invalidateMatchQueries = () => {
    // Invalidate the match overview query with the new key pattern
    queryClient.invalidateQueries({
      queryKey: ['supplier_rec_match_overview'],
    })
    // Also invalidate match by ID queries
    queryClient.invalidateQueries({
      queryKey: ['matchByIdQuery'],
    })
  }

  // Individual invoice actions
  const useVoidInvoice = () => {
    return $api.useMutation('post', '/supplier_rec/invoice/void', {
      onMutate: async (request) => {
        // Cancel any outgoing queries to prevent race conditions
        await cacheUtils.cancelMatchQueries()

        // Also cancel detail queries to prevent race conditions
        await queryClient.cancelQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        // Snapshot previous data for rollback
        const previousData = cacheUtils.getPreviousQueryData()
        const previousDetailData = queryClient.getQueriesData({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        // Extract invoice ID from request (could be in params.query or body)
        const invoiceId = request.params?.query?.invoice_id || request.body?.invoice_id

        // Find the match that contains this invoice for status transition
        let matchToVoid: string | null = null
        let originalStatus = 'unmatched'
        const queries = ['open', 'approved'] as const

        for (const queryType of queries) {
          const queryKey = [`supplier_rec_match_overview_${queryType}`]
          const queryData = queryClient.getQueryData(queryKey) as MatchQueryData | undefined

          if (queryData?.rows) {
            const match = queryData.rows.find((row: any) =>
              row.invoice_numbers?.some((inv: any) => inv.id === invoiceId)
            )

            if (match) {
              matchToVoid = match.id
              originalStatus = match.status
              break
            }
          }
        }

        // Apply optimistic void update with status transition
        if (matchToVoid) {
          const optimisticUpdate = cacheUtils.createOptimisticVoid(matchToVoid)
          cacheUtils.updateMatchStatusTransition(
            [matchToVoid],
            originalStatus,
            'void',
            optimisticUpdate.rowsToUpdate || []
          )
        }

        // Optimistically update detail cache to show voided status
        queryClient.setQueriesData(
          { queryKey: ['post', '/supplier_rec/match/detail'] },
          (oldData: MatchDetailData | undefined) => {
            if (!oldData || !invoiceId) return oldData

            // Add invoice to void_invoices array if not already there
            const currentVoidInvoices = oldData.void_invoices || []
            const isAlreadyVoid = currentVoidInvoices.some((vi) => vi._id === invoiceId || vi.invoice_id === invoiceId)

            if (!isAlreadyVoid) {
              // Find invoice in regular invoices to add to void list
              const invoiceToVoid = oldData.invoice_documents?.find(
                (inv) => inv.document_id === invoiceId || inv._id === invoiceId
              )

              if (invoiceToVoid) {
                return {
                  ...oldData,
                  void_invoices: [
                    ...currentVoidInvoices,
                    {
                      _id: invoiceToVoid.document_id || invoiceToVoid._id,
                      invoice_id: invoiceToVoid.document_id || invoiceToVoid._id,
                      invoice_number: invoiceToVoid.document_number,
                      total: invoiceToVoid.total,
                      invoice_date: invoiceToVoid.document_date,
                    },
                  ],
                }
              }
            }

            return oldData
          }
        )

        return { previousData, previousDetailData, request }
      },
      onSuccess: (response, request, context) => {
        // Process API response and update cache with real data
        const cacheUpdate = cacheUtils.processApiResponse(response, 'void')

        if (cacheUpdate.rowsToUpdate?.length > 0) {
          // For void actions, we need to handle the status transition properly
          const updatedMatch = cacheUpdate.rowsToUpdate[0]

          if (updatedMatch.status === 'void') {
            // Use the status transition function to ensure proper cache movement
            cacheUtils.updateMatchStatusTransition(
              [updatedMatch.id],
              'unmatched', // original status doesn't matter since we're replacing with server data
              'void',
              cacheUpdate.rowsToUpdate
            )
          } else {
            // Fallback to regular cache update
            cacheUtils.updateMatchOverviewCache(cacheUpdate)
          }
        }

        // Invalidate detail cache to ensure fresh data
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        // Show success message
        message.success('Invoice voided successfully')
      },
      onError: (error, request, context: InvoiceActionContext | undefined) => {
        // Rollback optimistic update on error
        if (context?.previousData) {
          cacheUtils.rollbackQueryData(context.previousData)
        }

        // Rollback detail cache optimistic update on error
        if (context?.previousDetailData && context.previousDetailData.length > 0) {
          context.previousDetailData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey as string[], data)
          })
        }

        // Show error message
        message.error('Failed to void invoice. Please try again.')
      },
    })
  }

  const useUnvoidInvoice = () => {
    return $api.useMutation('post', '/supplier_rec/invoice/unvoid', {
      onMutate: async (request) => {
        // Cancel any outgoing queries to prevent race conditions
        await cacheUtils.cancelMatchQueries()

        // Also cancel detail queries to prevent race conditions
        await queryClient.cancelQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        // Snapshot previous data for rollback
        const previousData = cacheUtils.getPreviousQueryData()
        const previousDetailData = queryClient.getQueriesData({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        // Extract invoice ID from request (could be in params.query or body)
        const invoiceId = request.params?.query?.invoice_id || request.body?.invoice_id

        // Find the match that contains this invoice for status transition
        let matchToUnvoid: string | null = null
        const queryKey = [`supplier_rec_match_overview_voided`]
        const queryData = queryClient.getQueryData(queryKey) as MatchQueryData | undefined

        if (queryData?.rows) {
          const match = queryData.rows.find((row: any) => row.invoice_numbers?.some((inv: any) => inv.id === invoiceId))

          if (match) {
            matchToUnvoid = match.id
          }
        }

        // Apply optimistic unvoid update with status transition
        if (matchToUnvoid) {
          const optimisticUpdate = cacheUtils.createOptimisticUnvoid(matchToUnvoid)
          cacheUtils.updateMatchStatusTransition(
            [matchToUnvoid],
            'void',
            'unmatched',
            optimisticUpdate.rowsToUpdate || []
          )
        }

        // Add a small delay to ensure loading state is visible before optimistic update
        await new Promise((resolve) => setTimeout(resolve, 200))

        // Optimistically update detail cache to remove voided status
        queryClient.setQueriesData(
          { queryKey: ['post', '/supplier_rec/match/detail'] },
          (oldData: MatchDetailData | undefined) => {
            if (!oldData || !invoiceId) return oldData

            // Remove invoice from void_invoices array
            const currentVoidInvoices = oldData.void_invoices || []
            const filteredVoidInvoices = currentVoidInvoices.filter(
              (vi) => vi._id !== invoiceId && vi.invoice_id !== invoiceId
            )

            return {
              ...oldData,
              void_invoices: filteredVoidInvoices,
            }
          }
        )

        return { previousData, previousDetailData, request }
      },
      onSuccess: (response, request, context) => {
        // Process API response and update cache with real data
        const cacheUpdate = cacheUtils.processApiResponse(response, 'unvoid')
        if (cacheUpdate.rowsToUpdate?.length > 0) {
          // For unvoid actions, we need to handle the status transition properly
          const updatedMatch = cacheUpdate.rowsToUpdate[0]
          if (updatedMatch.status !== 'void') {
            // Use the status transition function to ensure proper cache movement
            cacheUtils.updateMatchStatusTransition(
              [updatedMatch.id],
              'void',
              updatedMatch.status,
              cacheUpdate.rowsToUpdate
            )
          } else {
            // Fallback to regular cache update
            cacheUtils.updateMatchOverviewCache(cacheUpdate)
          }
        }

        // Invalidate detail cache to ensure fresh data
        queryClient.invalidateQueries({
          queryKey: ['post', '/supplier_rec/match/detail'],
        })

        // Show success message
        message.success('Invoice restored successfully')
      },
      onError: (error, request, context) => {
        // Rollback optimistic update on error
        if (context?.previousData) {
          cacheUtils.rollbackQueryData(context.previousData)
        }

        // Rollback detail cache optimistic update on error
        if (context?.previousDetailData && context.previousDetailData.length > 0) {
          context.previousDetailData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey as string[], data)
          })
        }

        // Show error message
        message.error('Failed to restore invoice. Please try again.')
      },
    })
  }

  const useCreateManualInvoice = () => {
    return $api.useMutation('post', '/supplier_rec/invoice/create_manual', {
      onMutate: async (request) => {
        // Cancel any outgoing queries to prevent race conditions
        await cacheUtils.cancelMatchQueries()

        // Snapshot previous data for rollback
        const previousData = cacheUtils.getPreviousQueryData()

        // For manual invoice creation, we can optimistically add a placeholder row
        // Create a temporary match row based on invoice data
        const invoiceData = request.body as InvoiceCreationData | undefined
        const tempId = `temp_${Date.now()}`
        if (invoiceData) {
          const invoiceDate = new Date(invoiceData.invoice_date)
          const dueDate = new Date(invoiceData.due_date_utc)
          const today = new Date()
          const daysDiff = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

          // Extract products from details array
          const products =
            invoiceData.details
              ?.filter((detail) => detail.type === 'supply' && detail.product)
              ?.map((detail) => detail.product?.internal_name || detail.product?.source_name)
              ?.filter(Boolean) || []

          // Extract terminal info
          const terminalName = invoiceData.terminal?.internal_name || invoiceData.terminal?.source_name
          const terminals = terminalName ? [terminalName] : []

          // Extract BOL numbers from product lines only (not taxes)
          const bolNumbers =
            invoiceData.details
              ?.filter((detail) => detail.type === 'supply')
              ?.map((detail) => detail.bol_number)
              ?.filter(Boolean) || []

          const tempMatch = {
            match_id: tempId,
            id: tempId,
            supplier_id: invoiceData.supplier?.internal_id || 'unknown',
            supplier_name:
              invoiceData.supplier?.internal_name || invoiceData.supplier?.source_name || 'Unknown Supplier',
            status: 'unmatched',
            total_amount: invoiceData.details?.reduce((sum, detail) => sum + (detail.total || 0), 0) || 0,
            has_variances: false,
            invoice_numbers: [
              {
                id: `temp_inv_${Date.now()}`,
                number: invoiceData.invoice_number || 'Pending...',
                source: invoiceData.source_name || 'manual_entry',
              },
            ],
            bol_numbers: bolNumbers.map((number: string) => ({
              number: number,
            })),
            invoice_total: invoiceData.details?.reduce((sum, detail) => sum + (detail.total || 0), 0) || 0,
            bol_total: null,
            invoice_date: invoiceData.invoice_date,
            invoice_due_date: invoiceData.due_date_utc,
            days_until_due: daysDiff,
            source_name: invoiceData.source_name || 'manual_entry',
            products: products,
            terminals: terminals,
            destinations: [],
            _isOptimistic: true,
            _isCreating: true,
          }

          // Add optimistic row to cache
          const optimisticUpdate = {
            rowsToAdd: [tempMatch],
            rowsToUpdate: [],
            rowsToRemove: [],
          }
          cacheUtils.updateMatchOverviewCache(optimisticUpdate)
        }

        return { previousData, request, tempId }
      },
      onSuccess: (response, request, context) => {
        // Replace optimistic entry with real match data
        if (context?.tempId && response?.id) {
          const cacheUpdate = {
            rowsToRemove: [context.tempId],
            rowsToAdd: [response],
          }
          cacheUtils.updateMatchOverviewCache(cacheUpdate)
        } else {
          // Fallback to original behavior
          const cacheUpdate = cacheUtils.processApiResponse(response, 'link')
          cacheUtils.updateMatchOverviewCache(cacheUpdate)
        }

        // Show success message
        message.success('Invoice created successfully')
      },
      onError: (error, request, context) => {
        // Rollback optimistic update on error
        if (context?.previousData) {
          cacheUtils.rollbackQueryData(context.previousData)
        }

        // Extract and display the specific error message from API response
        let errorMessage = 'Failed to create invoice. Please try again.'

        // Handle multiple possible error structures
        if (error && typeof error === 'object') {
          // Try different possible error structures
          const anyError = error as any

          // OpenAPI React Query error structure
          if (anyError.error?.detail) {
            errorMessage = anyError.error.detail
          }
          // Standard axios/fetch error structure
          else if (anyError.response?.data?.detail) {
            errorMessage = anyError.response.data.detail
          }
          // Direct detail property
          else if (anyError.detail) {
            errorMessage = anyError.detail
          }
          // Message property
          else if (anyError.message) {
            errorMessage = anyError.message
          }
          // For debugging - log the error structure
          console.log('API Error structure:', JSON.stringify(error, null, 2))
        } else if (error instanceof Error) {
          errorMessage = error.message
        }

        message.error(errorMessage)
      },
    })
  }

  const useCreateSupplierInvoiceV1 = () => {
    return $api.useMutation('post', '/v1/supplier_reconciliation/invoice/create', {
      onMutate: async (request) => {
        // Cancel any outgoing queries to prevent race conditions
        await cacheUtils.cancelMatchQueries()

        // Snapshot previous data for rollback
        const previousData = cacheUtils.getPreviousQueryData()

        // For V1 invoice creation, we can optimistically add a placeholder row
        // Create a temporary match row based on invoice data
        const invoiceData = request.body as InvoiceCreationData | undefined
        const tempId = `temp_v1_${Date.now()}`
        if (invoiceData) {
          const invoiceDate = new Date(invoiceData.invoice_date)
          const dueDate = new Date(invoiceData.due_date_utc)
          const today = new Date()
          const daysDiff = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

          // Extract products from details array
          const products =
            invoiceData.details
              ?.filter((detail) => detail.type === 'supply' && detail.product)
              ?.map((detail) => detail.product?.internal_name || detail.product?.source_name)
              ?.filter(Boolean) || []

          // Extract terminal info
          const terminalName = invoiceData.terminal?.internal_name || invoiceData.terminal?.source_name
          const terminals = terminalName ? [terminalName] : []

          // Extract BOL numbers from product lines only (not taxes)
          const bolNumbers =
            invoiceData.details
              ?.filter((detail) => detail.type === 'supply')
              ?.map((detail) => detail.bol_number)
              ?.filter(Boolean) || []

          const tempMatch = {
            match_id: tempId,
            id: tempId,
            supplier_id: invoiceData.supplier?.internal_id || 'unknown',
            supplier_name:
              invoiceData.supplier?.internal_name || invoiceData.supplier?.source_name || 'Unknown Supplier',
            status: 'unmatched',
            total_amount: invoiceData.details?.reduce((sum, detail) => sum + (detail.total || 0), 0) || 0,
            has_variances: false,
            invoice_numbers: [
              {
                id: `temp_inv_v1_${Date.now()}`,
                number: invoiceData.invoice_number || 'Pending...',
                source: invoiceData.source_name || 'manual_entry',
              },
            ],
            bol_numbers: bolNumbers.map((number: string) => ({
              number: number,
            })),
            invoice_total: invoiceData.details?.reduce((sum, detail) => sum + (detail.total || 0), 0) || 0,
            bol_total: null,
            invoice_date: invoiceData.invoice_date,
            invoice_due_date: invoiceData.due_date_utc,
            days_until_due: daysDiff,
            source_name: invoiceData.source_name || 'manual_entry',
            products: products,
            terminals: terminals,
            destinations: [],
            _isOptimistic: true,
            _isCreating: true,
          }

          // Add optimistic row to cache
          const optimisticUpdate = {
            rowsToAdd: [tempMatch],
            rowsToUpdate: [],
            rowsToRemove: [],
          }
          cacheUtils.updateMatchOverviewCache(optimisticUpdate)
        }

        return { previousData, request, tempId }
      },
      onSuccess: (response, request, context) => {
        // Replace optimistic entry with real match data
        if (context?.tempId && response?.id) {
          const cacheUpdate = {
            rowsToRemove: [context.tempId],
            rowsToAdd: [response],
          }
          cacheUtils.updateMatchOverviewCache(cacheUpdate)
        } else {
          // Fallback to original behavior
          const cacheUpdate = cacheUtils.processApiResponse(response, 'link')
          cacheUtils.updateMatchOverviewCache(cacheUpdate)
        }

        // Show success message
        message.success('Invoice created successfully')
      },
      onError: (error, request, context) => {
        // Rollback optimistic update on error
        if (context?.previousData) {
          cacheUtils.rollbackQueryData(context.previousData)
        }

        // Extract and display the specific error message from API response
        let errorMessage = 'Failed to create invoice. Please try again.'

        // Handle multiple possible error structures
        if (error && typeof error === 'object') {
          // Try different possible error structures
          const anyError = error as any

          // OpenAPI React Query error structure
          if (anyError.error?.detail) {
            errorMessage = anyError.error.detail
          }
          // Standard axios/fetch error structure
          else if (anyError.response?.data?.detail) {
            errorMessage = anyError.response.data.detail
          }
          // Direct detail property
          else if (anyError.detail) {
            errorMessage = anyError.detail
          }
          // Message property
          else if (anyError.message) {
            errorMessage = anyError.message
          }
          // For debugging - log the error structure
          console.log('API Error structure:', JSON.stringify(error, null, 2))
        } else if (error instanceof Error) {
          errorMessage = error.message
        }

        message.error(errorMessage)
      },
    })
  }

  // Get metadata for invoice creation
  const useMetadata = () => {
    return useMasterMetadata<InvoiceCreationMetadataResponse>('invoice_creation')
  }

  // High-level invoice action handlers
  const invoiceActionHandlers = {
    /**
     * Void an invoice
     */
    voidInvoice: async (invoiceId: string, reason: string) => {
      const mutation = useVoidInvoice()
      return mutation.mutateAsync({
        body: {
          invoice_id: invoiceId,
          reason: reason,
        },
      })
    },

    /**
     * Unvoid an invoice
     */
    unvoidInvoice: async (invoiceId: string) => {
      const mutation = useUnvoidInvoice()
      return mutation.mutateAsync({
        body: {
          invoice_id: invoiceId,
        },
      })
    },

    /**
     * Create a manual invoice
     */
    createManualInvoice: async (invoiceData: CreateManualInvoiceRequest) => {
      const mutation = useCreateManualInvoice()
      return mutation.mutateAsync({
        body: invoiceData,
      })
    },
  }

  return {
    // Individual mutation hooks
    useVoidInvoice,
    useUnvoidInvoice,
    useCreateManualInvoice,
    useCreateSupplierInvoiceV1,
    useMetadata,

    // High-level handlers
    ...invoiceActionHandlers,

    // Utility
    invalidateMatchQueries,
  }
}
